import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { notificationsAPI } from '../services/api';
import { reconcilePendingAcks } from '../lib/badgeReconcile';

/**
 * NotificationProvider — the ONE poller for every badge AND the bell list.
 *
 * WHY it exists
 *   The old sidebar ran ~6 pollers on different clocks (bell every 12s, chat
 *   unread every 20-30s, verification stats every 20-30s, staff badges every
 *   45s, …). Badges were inconsistent, slow, and "checking" a badge didn't
 *   clear it — the next poll would eventually reflect the truth, or not.
 *
 * WHAT this owns now
 *   • Polls GET /api/badges every POLL_MS (5s) — all sidebar/bell counts.
 *   • Polls GET /api/notifications every POLL_MS — the bell-dropdown list AND
 *     the source-of-truth for staff-side per-type badges (assignment /
 *     announcement / heat). Same clock; no drift.
 *   • Exposes { badges, notifications, ackCategory, markBellRead, refresh }.
 *   • ackCategory / markBellRead are OPTIMISTIC UI: they zero the local badge
 *     immediately, fire the network call, and revert on failure. That's what
 *     makes "checked → clears" feel instant regardless of poll timing.
 *
 * Mount ONCE, high in the tree (App.js root). Every downstream badge reads
 * from this via useBadges().
 */

// 20s, not 5s. The poll does NOT drive your own clicks — ackCategory /
// markBellRead update the badge optimistically and revert on failure — so
// this only sets how quickly another person's action surfaces on your screen.
// At 5s that cost ~1,440 requests an hour per open tab, forever.
const POLL_MS = 20000;
const EMPTY_BADGES = {
  bell: 0, chat: 0, verifications: 0,
  missing_pay: 0, leads: 0, payouts: 0, team: 0, at: null,
};

const NotificationContext = createContext({
  badges: EMPTY_BADGES,
  notifications: [],
  ackCategory: async () => {},
  markBellRead: async () => {},
  refresh: async () => {},
});

export function NotificationProvider({ children }) {
  const [badges, setBadges] = useState(EMPTY_BADGES);
  const [notifications, setNotifications] = useState([]);
  const alive = useRef(true);
  // Categories that have been optimistically acked but whose /api/badges
  // response hasn't caught up yet. A stale in-flight poll can arrive AFTER
  // the ack POST but BEFORE the server-side lastSeenAt propagates — without
  // this set the badge briefly flickers back on. Cleared per-category once
  // the server reports 0 for it.
  const pendingAckRef = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      // Fire both in parallel — same clock, single poller from the app's POV.
      const [badgeData, notifData] = await Promise.all([
        notificationsAPI.getBadges().catch(() => null),
        notificationsAPI.getAll(50).catch(() => null),
      ]);
      if (!alive.current) return;
      if (badgeData) {
        // Reconcile against pending acks (shared helper — see
        // lib/badgeReconcile.js). Suppresses a stale poll count for a
        // category the user just acked, and self-heals once the server
        // catches up.
        const { badges: merged, pending: nextPending } = reconcilePendingAcks(
          badgeData,
          pendingAckRef.current,
        );
        pendingAckRef.current = nextPending;
        setBadges(merged);
      }
      if (notifData) {
        const items = Array.isArray(notifData) ? notifData : (notifData.notifications || []);
        setNotifications(items);
      }
    } catch (_err) {
      // Silent — the poller must never disrupt the UI on a transient failure.
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    // Coalesce the return-to-tab triggers. `focus` already existed and
    // `visibilitychange` is the reliable one for tab switching, but firing
    // both on the same return would issue two identical requests
    // milliseconds apart — the exact pattern I spent a measurement pass
    // proving this app did NOT have. Do not add one now.
    //
    // S116 — the INITIAL load now runs through this same coalesce guard too.
    // Previously the mount called refresh() DIRECTLY (bypassing the guard), so
    // the `focus` + `visibilitychange` the browser fires immediately after a
    // fresh load each triggered ANOTHER full refresh — /notifications AND
    // /badges fired ~2× apiece (~4 "notification checks" on load). Routing the
    // first refresh through refreshCoalesced absorbs those into ONE.
    let lastRefreshAt = 0;
    const refreshCoalesced = () => {
      const now = Date.now();
      if (now - lastRefreshAt < 2000) return;
      lastRefreshAt = now;
      refresh();
    };
    // One-time-per-user baseline call so the pre-existing backlog stops
    // showing as a badge. Idempotent server-side — safe to call every mount.
    (async () => {
      try { await notificationsAPI.ackBaseline(); } catch (_e) { /* silent */ }
      refreshCoalesced();
    })();
    const tick = () => {
      // A hidden tab's badge count matters to nobody. The browser throttles
      // this unevenly on its own; that is not a reason for the app to keep
      // asking.
      if (document.visibilityState !== 'visible') return;
      refreshCoalesced();
    };
    const id = setInterval(tick, POLL_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') refreshCoalesced(); };
    const onVisible = () => { if (document.visibilityState === 'visible') refreshCoalesced(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive.current = false;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const ackCategory = useCallback(async (category) => {
    if (!category) return;
    setBadges((prev) => ({ ...prev, [category]: 0 }));
    pendingAckRef.current.add(category);
    try {
      await notificationsAPI.ackCategory(category);
    } catch (_err) {
      refresh();
    }
  }, [refresh]);

  const markBellRead = useCallback(async () => {
    setBadges((prev) => ({ ...prev, bell: 0 }));
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await notificationsAPI.markAllAsRead();
    } catch (_err) {
      refresh();
    }
  }, [refresh]);

  return (
    <NotificationContext.Provider
      value={{ badges, notifications, ackCategory, markBellRead, refresh }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useBadges() {
  return useContext(NotificationContext);
}
